import { motion } from "framer-motion";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, TrendingUp, AlertTriangle, Lock, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface TipProps {
  league: string;
  homeTeam: string;
  awayTeam: string;
  prediction: string;
  odds: string;
  confidence: "High" | "Medium" | "Risky";
  time: string;
  status?: "Won" | "Lost" | "Pending";
  analysis?: string;
  isPremium?: boolean;
  isLocked?: boolean;
  oddsSource?: "market" | "calculated" | "none";
  oddsLastUpdated?: string;
}

function formatMatchTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();

  const isToday = date.toDateString() === now.toDateString();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const isTomorrow = date.toDateString() === tomorrow.toDateString();

  const timeStr = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

  if (isToday) {
    return `Today, ${timeStr}`;
  } else if (isTomorrow) {
    return `Tomorrow, ${timeStr}`;
  } else {
    const dateStr = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    return `${dateStr}, ${timeStr}`;
  }
}

function formatTimeSince(isoString: string | undefined): string {
  if (!isoString) return '';
  
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  
  if (diffSecs < 60) {
    return `${diffSecs}s ago`;
  } else if (diffSecs < 3600) {
    return `${Math.floor(diffSecs / 60)}m ago`;
  } else {
    return `${Math.floor(diffSecs / 3600)}h ago`;
  }
}

export function TipCard({
  league,
  homeTeam,
  awayTeam,
  prediction,
  odds,
  confidence,
  time,
  status = "Pending",
  analysis,
  isPremium = false,
  isLocked = false,
  oddsSource = "calculated",
  oddsLastUpdated
}: TipProps) {
  const confidenceColor = {
    High: "text-primary bg-primary/10 border-primary/20",
    Medium: "text-yellow-500 bg-yellow-500/10 border-yellow-500/20",
    Risky: "text-red-500 bg-red-500/10 border-red-500/20",
  }[confidence];

  return (
    <motion.div whileHover={{ y: -5 }} className="h-full">
      <Card className={`glass h-full overflow-hidden group relative ${isLocked ? 'opacity-75' : ''}`}>
        <CardHeader className="pb-2 border-b border-white/5 bg-white/5">
          <div className="flex items-center justify-between text-xs font-medium text-muted-foreground uppercase tracking-wider">
            <span>{league}</span>
            <div className="flex items-center gap-2">
              <span>{formatMatchTime(time)}</span>
              {isPremium && (
                <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-bold text-xs">
                  PRO
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          {isLocked ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Lock className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-2">Premium Tip</p>
              <p className="text-sm text-muted-foreground/70">Upgrade to unlock this prediction</p>
            </div>
          ) : (
            <>
              <div className="flex justify-between items-center mb-6">
                <div className="text-center flex-1">
                  <h3 className="text-lg font-bold text-white mb-1">{homeTeam}</h3>
                </div>
                <div className="px-4 text-xs font-bold text-muted-foreground">VS</div>
                <div className="text-center flex-1">
                  <h3 className="text-lg font-bold text-white mb-1">{awayTeam}</h3>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/5">
                  <span className="text-sm text-muted-foreground">Prediction</span>
                  <span className="font-bold text-white">{prediction}</span>
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/5">
                  <span className="text-sm text-muted-foreground">Odds</span>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-primary text-xl">{odds}</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge 
                          variant="outline" 
                          className={`text-xs cursor-help ${
                            oddsSource === 'market' 
                              ? 'bg-green-500/10 border-green-500/30 text-green-400' 
                              : 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400'
                          }`}
                        >
                          {oddsSource === 'market' ? 'Market' : 'Estimated'}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        <p className="text-xs">
                          {oddsSource === 'market' 
                            ? 'These are real odds from bookmakers (Bet365, etc.)' 
                            : 'These are AI-calculated odds based on probability (1/probability). Real market odds unavailable for this prediction.'}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                    {oddsLastUpdated && oddsSource === 'market' && (
                      <span className="text-xs text-muted-foreground">
                        {formatTimeSince(oddsLastUpdated)}
                      </span>
                    )}
                  </div>
                </div>

                {analysis && (
                  <div className="p-3 rounded-lg bg-primary/5 border border-primary/10">
                    <div className="flex items-start gap-2">
                      <Info className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-muted-foreground leading-relaxed">{analysis}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between mt-4">
                  <Badge variant="outline" className={`rounded-full px-3 py-1 ${confidenceColor}`}>
                    {confidence === 'High' && <CheckCircle2 className="w-3 h-3 mr-1" />}
                    {confidence === 'Medium' && <TrendingUp className="w-3 h-3 mr-1" />}
                    {confidence === 'Risky' && <AlertTriangle className="w-3 h-3 mr-1" />}
                    {confidence} Confidence
                  </Badge>

                  {status === "Won" && (
                    <Badge className="bg-primary text-primary-foreground font-bold">WON</Badge>
                  )}
                  {status === "Lost" && (
                    <Badge className="bg-red-500 text-white font-bold">LOST</Badge>
                  )}
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
